import { z } from "zod";

import { formatDateMmDdYyyy, normalizeSimpleDate } from "@/src/lib/utils/date-and-phone-utils";
import { Classification } from "@/src/models/constants/classifications";
import { RecordRequestSource } from "@/src/models/constants/record-request-source";
import { Section } from "@/src/models/section";

const CONFLUENT_ALL_STAR_EMAIL_ADDRESSES = {
  medicalRecords: "medicalrecords@goconfluent.com",
  credentialing: "credentialingsupport@goconfluent.com",
  grievances: "grievances@allstarphysicaltherapy.org",
  // Payer / insurance bulletins (non-patient-specific) are emailed to All Star, never synced.
  insuranceBulletins: "cinman@allstarphysicaltherapy.org",
} as const;

const RECIPIENT_BY_CLASSIFICATION: Partial<Record<Classification, string>> = {
  [Classification.LEGAL_SUBPOENA]: CONFLUENT_ALL_STAR_EMAIL_ADDRESSES.medicalRecords,
  [Classification.ADMIN_CREDENTIAL]: CONFLUENT_ALL_STAR_EMAIL_ADDRESSES.credentialing,
  [Classification.LEGAL_GRIEVANCE]: CONFLUENT_ALL_STAR_EMAIL_ADDRESSES.grievances,
  // ADMIN is used here as the payer-bulletin bucket (see classification-prompts.ts / document-mappings.ts).
  [Classification.ADMIN]: CONFLUENT_ALL_STAR_EMAIL_ADDRESSES.insuranceBulletins,
};

export const CONFLUENT_ALL_STAR_EMAIL_ONLY_CLASSIFICATIONS = new Set<Classification>([
  Classification.LEGAL_SUBPOENA,
  Classification.ADMIN_CREDENTIAL,
  Classification.LEGAL_GRIEVANCE,
  Classification.ADMIN,
]);

export function getConfluentAllStarEmailRecipients(section: Section): string[] {
  const classification = section.uploadClassification || section.classification;
  const recipient = RECIPIENT_BY_CLASSIFICATION[classification];

  return recipient ? [recipient] : [];
}

// Recipient for insurance communications whose patient is not found in Clinicient (emailed to All Star
// from the INSURANCE_COMMUNICATION workflow instead of being filed to a chart).
export const CONFLUENT_ALL_STAR_INSURANCE_COMMUNICATION_EMAIL = CONFLUENT_ALL_STAR_EMAIL_ADDRESSES.insuranceBulletins;

const insuranceCommunicationSubjectSchema = z.object({ dateReceived: z.string().nullish() });

/**
 * Subject for a patient-not-found insurance communication email: "MM/DD/YYYY Insurance Communication".
 * Returns null for every other classification (subpoena / credentialing / grievance / admin, etc.) so
 * the caller falls back to the default doc-routing subject — this override is INSURANCE_COMMUNICATION only.
 */
export function getConfluentAllStarDocRoutingEmailSubject(section: Section): string | null {
  const classification = section.uploadClassification || section.classification;

  if (classification !== Classification.INSURANCE_COMMUNICATION) return null;

  const reviewed = insuranceCommunicationSubjectSchema.safeParse(section.reviewedContent ?? {});
  const parsedDate = reviewed.success
    ? z.iso.date().safeParse(normalizeSimpleDate(reviewed.data.dateReceived?.trim()))
    : null;
  const prefix = parsedDate?.success ? `${formatDateMmDdYyyy(parsedDate.data)} ` : "";

  return `${prefix}Insurance Communication`;
}

export type ConfluentAllStarRecordRequestRoute = "third_party" | "provider" | "needs_review";

export function resolveConfluentAllStarRecordRequestRoute(section: Section): ConfluentAllStarRecordRequestRoute {
  const reviewedContent = (section.reviewedContent ?? {}) as Record<string, unknown>;

  switch (reviewedContent.recordRequestSource) {
    case RecordRequestSource.OTHER:
      return "third_party";
    case RecordRequestSource.WORKERS_COMP:
    case RecordRequestSource.HOSPITAL:
    case RecordRequestSource.CLINIC:
      return "provider";
    default:
      return "needs_review";
  }
}
